import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, Users, Trophy, Calendar, TrendingUp, Activity } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const checkAdminAndFetchStats = async () => {
      try {
        // First check if user is admin
        const checkResponse = await axios.get(`${API}/admin/check`, {
          headers: getAuthHeader()
        });
        
        if (!checkResponse.data.is_admin) {
          toast.error('Admin access required');
          navigate('/dashboard');
          return;
        }
        
        setIsAdmin(true);
        
        // Fetch stats
        const statsResponse = await axios.get(`${API}/admin/stats`, {
          headers: getAuthHeader()
        });
        setStats(statsResponse.data);
      } catch (error) {
        console.error('Admin check failed:', error);
        toast.error('Access denied');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    checkAdminAndFetchStats();
  }, [getAuthHeader, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="retro-heading text-ink text-lg">Loading admin panel...</div>
      </div>
    );
  }

  if (!isAdmin || !stats) {
    return null;
  }

  return (
    <div className="min-h-screen bg-cream p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="pixel-btn p-2"
            data-testid="back-btn"
          >
            <ChevronLeft size={20} />
          </button>
          <h1 className="retro-heading text-lg md:text-xl text-ink">
            ADMIN DASHBOARD
          </h1>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
          <div className="pixel-card text-center">
            <Users className="mx-auto mb-2 text-grass" size={32} />
            <p className="retro-heading text-2xl md:text-3xl text-ink">{stats.total_users}</p>
            <p className="retro-body text-sm text-dirt">Total Users</p>
          </div>
          
          <div className="pixel-card text-center">
            <Trophy className="mx-auto mb-2 text-grass" size={32} />
            <p className="retro-heading text-2xl md:text-3xl text-ink">{stats.total_games}</p>
            <p className="retro-body text-sm text-dirt">Games Played</p>
          </div>
          
          <div className="pixel-card text-center">
            <Calendar className="mx-auto mb-2 text-grass" size={32} />
            <p className="retro-heading text-2xl md:text-3xl text-ink">{stats.games_today}</p>
            <p className="retro-body text-sm text-dirt">Games Today</p>
          </div>
          
          <div className="pixel-card text-center">
            <Activity className="mx-auto mb-2 text-grass" size={32} />
            <p className="retro-heading text-2xl md:text-3xl text-ink">{stats.games_this_week}</p>
            <p className="retro-body text-sm text-dirt">This Week</p>
          </div>
          
          <div className="pixel-card text-center">
            <TrendingUp className="mx-auto mb-2 text-grass" size={32} />
            <p className="retro-heading text-2xl md:text-3xl text-ink">{stats.avg_games_per_user}</p>
            <p className="retro-body text-sm text-dirt">Avg/User</p>
          </div>
          
          <div className="pixel-card text-center">
            <Users className="mx-auto mb-2 text-grass" size={32} />
            <p className="retro-heading text-2xl md:text-3xl text-ink">{stats.users_today}</p>
            <p className="retro-body text-sm text-dirt">New Today</p>
          </div>
        </div>

        {/* Available Playlists */}
        <div className="pixel-card mb-6">
          <h2 className="retro-heading text-sm text-ink mb-4">AVAILABLE PLAYLISTS</h2>
          <div className="space-y-2">
            {stats.playlists.map((playlist, index) => (
              <div key={playlist.id} className="flex items-center gap-2 p-2 bg-gray-100 border-2 border-black">
                <span className="retro-body text-lg text-grass font-bold">{index + 1}.</span>
                <span className="retro-body text-lg text-ink">{playlist.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Games */}
        <div className="pixel-card mb-6">
          <h2 className="retro-heading text-sm text-ink mb-4">RECENT GAMES</h2>
          {stats.recent_games.length === 0 ? (
            <p className="retro-body text-lg text-dirt text-center py-4">No games played yet</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {stats.recent_games.map((game) => (
                <div key={game.id} className="p-3 bg-gray-100 border-2 border-black">
                  <div className="flex justify-between items-center">
                    <span className="retro-body text-lg text-ink">
                      {game.team_a?.name || 'Team A'} vs {game.team_b?.name || 'Team B'}
                    </span>
                    <span className="retro-body text-sm text-grass font-bold">
                      {game.winner}
                    </span>
                  </div>
                  <p className="retro-body text-xs text-dirt mt-1">
                    {new Date(game.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Google Analytics Note */}
        <div className="pixel-card bg-gray-100">
          <h2 className="retro-heading text-xs text-ink mb-2">ANALYTICS</h2>
          <p className="retro-body text-sm text-dirt">
            Google Analytics (G-QKHP0GT2BV) is active. View detailed reports at{' '}
            <a 
              href="https://analytics.google.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-grass underline"
            >
              analytics.google.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
